import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

type Tone = 'primary' | 'accent' | 'sage';

const TONES: Record<Tone, { fill: string; on: string; glow: string }> = {
  primary: {
    fill: 'var(--color-primary-container)',
    on: 'var(--color-on-primary-container)',
    glow: 'color-mix(in oklab, var(--color-primary) 22%, transparent)',
  },
  accent: {
    fill: 'var(--color-accent-container)',
    on: 'var(--color-on-accent-container)',
    glow: 'color-mix(in oklab, var(--color-accent) 22%, transparent)',
  },
  sage: {
    fill: 'color-mix(in oklab, var(--color-sage) 20%, var(--color-surface))',
    on: 'var(--color-fg)',
    glow: 'color-mix(in oklab, var(--color-sage) 22%, transparent)',
  },
};

/**
 * Expressive stat tile: glass surface, tinted icon chip, large mono numeral.
 * Used on the dashboard and the remaining-space modal.
 */
export default function StatCard({
  label,
  value,
  icon,
  tone = 'primary',
}: {
  label: string;
  value: ReactNode;
  icon?: string; // FontAwesome class, e.g. "fa-flask"
  tone?: Tone;
}) {
  const t = TONES[tone];
  return (
    <Box
      className="glass"
      sx={{
        flex: '1 1 180px',
        minWidth: 170,
        borderRadius: 'var(--radius-md)',
        p: 2.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        boxShadow: 'var(--shadow-e1)',
        transition: 'transform 0.25s var(--ease-out-soft), box-shadow 0.25s var(--ease-out-soft)',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: 'var(--shadow-e2)' },
      }}
    >
      {icon && (
        <Box
          sx={{
            flexShrink: 0,
            width: 46,
            height: 46,
            borderRadius: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: t.fill,
            color: t.on,
            boxShadow: `0 6px 18px -8px ${t.glow}`,
          }}
        >
          <i className={`fas ${icon}`} style={{ fontSize: '1.1rem' }} />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          className="font-mono"
          sx={{
            fontWeight: 700,
            fontSize: '1.7rem',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: 'var(--color-fg)',
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            mt: 0.6,
            fontSize: '0.72rem',
            fontWeight: 600,
            color: 'var(--color-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}
